import { useEffect, useState, useCallback } from "react";
import { Ticket, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/lib/auth";

const API = process.env.REACT_APP_BACKEND_URL;
const POLL_MS = 60_000;
const WARN_HOURS = 48;

function hoursUntil(iso) {
  return (new Date(iso).getTime() - Date.now()) / 3_600_000;
}

/** Landing-only floating warning shown when the user has a ticket expiring in <48h.
 * Dismissible per-ticket via localStorage. Reactivates on new imminent expiries.
 */
export default function TicketExpiringBanner() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [target, setTarget] = useState(null); // { balance, next_expiry }

  const load = useCallback(async () => {
    if (!user) { setTarget(null); return; }
    try {
      const r = await fetch(`${API}/api/tickets/me`, { credentials: "include" });
      if (!r.ok) return;
      const d = await r.json();
      if (d.balance > 0 && d.next_expiry) setTarget(d);
      else setTarget(null);
    } catch { /* ignore */ }
  }, [user]);

  useEffect(() => { load(); const t = setInterval(load, POLL_MS); return () => clearInterval(t); }, [load]);

  if (!target) return null;
  const hrs = hoursUntil(target.next_expiry);
  if (hrs <= 0 || hrs > WARN_HOURS) return null;

  const key = `ufo_tk_warn_${target.next_expiry}`;
  if (typeof window !== "undefined" && localStorage.getItem(key) === "1") return null;

  const label = hrs < 1 ? t("ticket_expiring.minutes", { count: Math.max(1, Math.round(hrs * 60)) }) : t("ticket_expiring.hours", { count: Math.round(hrs) });
  const dismiss = (e) => {
    e.stopPropagation();
    localStorage.setItem(key, "1");
    setTarget(null);
  };

  return (
    <a
      href="/rooms"
      data-testid="ticket-expiring-banner"
      className="fixed bottom-24 right-4 z-[55] w-[calc(100%-2rem)] max-w-sm block cursor-pointer group"
    >
      <div className="rounded-sm border border-red-400/70 bg-gradient-to-br from-red-500/25 to-black/80 backdrop-blur-md shadow-2xl shadow-red-400/30 p-4 flex items-center gap-3 animate-in fade-in slide-in-from-right-4 duration-300">
        <div className="h-10 w-10 rounded-full border border-red-400 bg-red-500/20 text-red-200 flex items-center justify-center shrink-0">
          <Ticket className="h-5 w-5 animate-pulse" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-mono text-[9px] tracking-[0.25em] text-red-200/90">{t("ticket_expiring.label")}</p>
          <p className="font-display font-black text-lg tracking-tighter text-white truncate">
            {t("ticket_expiring.balance", { count: target.balance, time: label })}
          </p>
          <p className="font-mono text-[10px] tracking-widest text-red-200">{t("ticket_expiring.hint")}</p>
        </div>
        <button onClick={dismiss}
          data-testid="ticket-expiring-close"
          className="p-1 rounded-sm text-white/50 hover:text-white hover:bg-white/10 shrink-0">
          <X className="h-4 w-4" />
        </button>
      </div>
    </a>
  );
}
